import { Color } from '@persona-id/design-tokens';
import { orderBy as lodashOrderBy, uniq } from 'lodash/fp';
import { memo, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import TableLayout, {
  TableNullState,
  TableTD,
  TableTH,
  TableTR,
} from 'dashboard/layouts/TableLayout';
import {
  AssignmentDistributionDatum,
  CaseAnalyticsAssignmentDistribution,
} from 'dashboard/models/case-analytics';
import useToggle from 'lib/hooks/useToggle';

const TDContentContainer = styled('div')`
  display: grid;
  grid-template-columns: auto auto auto;
  align-items: center;
`;

const Percentage = styled('div')`
  color: ${Color.CoreSlate1000};
`;

export interface CaseAssignmentDistributionTableProps extends CaseAnalyticsAssignmentDistribution {
  isLoading: boolean;
}

const CaseAssignmentDistributionTable = ({
  data: initialData,
  isLoading,
}: CaseAssignmentDistributionTableProps) => {
  // We handle column header click sorting in line here because we don't want to force
  // the user to click "Run Query" after selecting a table header. Since the data is all
  // loaded and not paginated, we can sort the passed in data here.
  const [data, setData] = useState<AssignmentDistributionDatum[]>(
    initialData as AssignmentDistributionDatum[],
  );
  const [orderBy, setOrderBy] = useState('total_cases');
  const [shouldSortDescending, { toggle: toggleSortDescending, enable: sortDescending }] =
    useToggle(true);

  const handleColumnHeaderClick = (columnName: string) => {
    columnName === orderBy ? toggleSortDescending() : sortDescending();
    setOrderBy(columnName);
  };

  useEffect(() => {
    const orderedData = lodashOrderBy(
      [orderBy],
      [shouldSortDescending ? 'desc' : 'asc'],
      initialData,
    );
    setData(orderedData);
  }, [initialData, orderBy, shouldSortDescending]);

  const allDataStateNames: string[] = uniq(
    data.flatMap((datum) =>
      Object.keys(datum).filter(
        (name) => !['assignee_display_name', 'assignee_token', 'total_cases'].includes(name),
      ),
    ),
  );

  const stateTHs = useMemo(() => {
    return allDataStateNames.map((stateName, index) => {
      return (
        <TableTH
          key={index}
          onClick={() => handleColumnHeaderClick(stateName)}
          canSort={true}
          isSorted={orderBy === stateName}
          isDescending={shouldSortDescending}
          id={`case-analytics__header-${stateName}`}
          data-test={`case-analytics__header-${stateName}`}
        >
          {stateName}
        </TableTH>
      );
    });
  }, [allDataStateNames]);

  const rows = data.map((datum) => {
    const stateColumns = allDataStateNames.map((stateName, index) => {
      const statePercentage = (((datum[stateName] as number) / datum.total_cases) * 100).toFixed(0);

      return (
        <TableTD key={index}>
          <TDContentContainer>
            <div>{datum[stateName]}</div>
            <Percentage>{statePercentage}%</Percentage>
          </TDContentContainer>
        </TableTD>
      );
    });

    return (
      <TableTR
        data-test={`case-analytics__row--${datum.assignee_token}`}
        id={`case-analytics__row--${datum.assignee_token}`}
        key={datum.assignee_token}
      >
        <TableTD>{datum.assignee_display_name}</TableTD>
        {stateColumns}
        <TableTD>{datum.total_cases}</TableTD>
      </TableTR>
    );
  });

  return (
    <TableLayout
      dataTest='case-analytics__assignment-distribution--table'
      isLoading={isLoading}
      showHeader={true}
      isEmpty={data.length === 0}
      nullState={
        <TableNullState dataTestBlock='case-analytics__assignment-distribution--table'>
          No data found.
        </TableNullState>
      }
      headerTR={
        <>
          <TableTH data-test='case-analytics__assignee-header'>Name</TableTH>
          {stateTHs}
          <TableTH
            onClick={() => handleColumnHeaderClick('total_cases')}
            canSort={true}
            isSorted={orderBy === 'total_cases'}
            isDescending={shouldSortDescending}
            id='case-analytics__total-cases-header'
            data-test='case-analytics__total-cases-header'
          >
            Total Cases
          </TableTH>
        </>
      }
    >
      {rows}
    </TableLayout>
  );
};

export default memo(CaseAssignmentDistributionTable);
